import React, { useState, useEffect, useCallback, useContext } from "react";
import { Button, Input } from "reactstrap";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import Header from "../../layout/Header/Header";
import { AuthContext } from "../../contexts/AuthContext";
import { posGet, posPatch, avisaError, dinero, numero, hoy, sumaDias, fechaHora, variante } from "./posApi";
import { useTienda, TiendaSelector } from "./TiendaSelector";
import SinPermiso from "./SinPermiso";

const folioDe = (t) => `${t.origen && t.origen.clave ? `${t.origen.clave}-` : ""}${t.folio}`;
const nombre = (u) => (u ? `${u.nombre || ""} ${u.apellido || ""}`.trim() : "");

function PosFaltantes() {
  const { user } = useContext(AuthContext);
  const ctx = useTienda(true);
  const { tienda, nombreTienda, master } = ctx;
  const verCostos = !!(user && user.posCostos);
  const puedeAclarar = !!(master && user && user.posTraspasos);
  const [inicio, setInicio] = useState(sumaDias(hoy(), -30));
  const [fin, setFin] = useState(hoy());
  const [estado, setEstado] = useState("todos");
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);

  const cargar = useCallback(() => {
    if (!tienda) return;
    setCargando(true);
    posGet("/traspasos/faltantes", { area: tienda, inicio, fin, estado }).then(setDatos).catch(avisaError).finally(() => setCargando(false));
  }, [tienda, inicio, fin, estado]);
  useEffect(() => { cargar(); }, [cargar]);

  const aclarar = async (t) => {
    const r = await Swal.fire({
      title: `Aclarar faltante del traspaso ${folioDe(t)}`,
      html: `${t.origen.name} → ${t.destino.name}: faltaron <b>${numero(t.faltan)}</b> pzs.<br/><br/>Al aclarar, esas piezas <b>regresan al inventario de ${t.origen.name}</b>.`,
      input: "textarea", inputPlaceholder: "Qué pasó (ej. no se subieron a la camioneta, se quedaron en la tienda)",
      showCancelButton: true, confirmButtonText: "Marcar aclarado", cancelButtonText: "Cancelar",
      inputValidator: (v) => (!v || !v.trim() ? "Escribe qué se aclaró" : undefined),
    });
    if (!r.value) return;
    try {
      const res = await posPatch(`/traspasos/${t._id}/aclarar`, { nota: r.value });
      Swal.fire({ icon: "success", title: "Faltante aclarado", text: `${numero(res.regresadas)} pieza(s) regresaron a ${t.origen.name}.`, timer: 2500, showConfirmButton: false });
      cargar();
    } catch (e) { avisaError(e); }
  };

  const excel = () => {
    const filas = datos.traspasos.flatMap((t) => t.renglones.map((r) => ({
      Traspaso: folioDe(t), Enviado: fechaHora(t.fecha), Recibido: fechaHora(t.recibidoFecha), De: t.origen.name, A: t.destino.name,
      Envio: nombre(t.envio), Recibio: nombre(t.recibio), Codigo: r.codigo, Modelo: r.nombre, Color: r.color || "", Talla: r.talla || "",
      Enviadas: r.enviadas, Recibidas: r.recibidas, Faltan: r.faltan, ValorPrecio: r.valorPrecio, ...(verCostos ? { ValorCosto: r.valorCosto } : {}),
      ObservacionRecepcion: t.observacionesRecepcion || "", Aclarado: t.aclarado ? t.aclarado.nota : "",
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filas), "Faltantes");
    if (datos.rutas.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(datos.rutas.map((r) => ({ De: r.origen, A: r.destino, Traspasos: r.traspasos, Faltan: r.faltan, ValorPrecio: r.valorPrecio, ...(verCostos ? { ValorCosto: r.valorCosto } : {}), PorAclarar: r.pendientes }))), "Por ruta");
    XLSX.writeFile(wb, `faltantes_traspasos_${(nombreTienda || "tiendas").replace(/\s+/g, "_")}_${inicio}_${fin}.xlsx`);
  };

  if (!(user && user.rolTiendas && (user.posReportes || user.posTraspasos || user.posRecibir))) return <SinPermiso que="ver faltantes de traspasos" />;

  const t = datos && datos.totales;
  return (
    <>
      <Header />
      <div className="card container col-12" style={{ padding: "18px 22px" }}>
        <div className="page-header">
          <div>
            <h3 className="page-title"><i className="fas fa-search-minus mr-2" style={{ color: "#dc2626" }} />Faltantes de traspasos</h3>
            <p className="page-subtitle">Lo que una tienda envió y la otra no recibió · por fecha de envío</p>
          </div>
          <div className="page-actions">
            <Button color="secondary" size="sm" href="/PosTraspasos"><i className="fas fa-exchange-alt mr-1" />Traspasos</Button>
            <Button color="secondary" size="sm" href="/PuntoVenta"><i className="fas fa-th-large mr-1" />Menú</Button>
          </div>
        </div>

        <div className="filter-bar">
          <TiendaSelector ctx={ctx} permiteTodas />
          <div><label>Desde</label><Input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} /></div>
          <div><label>Hasta</label><Input type="date" value={fin} onChange={(e) => setFin(e.target.value)} /></div>
          <div><label>Mostrar</label>
            <Input type="select" value={estado} onChange={(e) => setEstado(e.target.value)}>
              <option value="todos">Todos</option><option value="pendientes">Por aclarar</option><option value="aclarados">Aclarados</option>
            </Input>
          </div>
          <div style={{ flexDirection: "row", gap: 8 }}>
            <Button color="primary" onClick={cargar} disabled={cargando}>{cargando ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-sync" />}</Button>{" "}
            <Button color="warning" onClick={excel} disabled={!datos || !datos.traspasos.length}><i className="fas fa-file-excel mr-1" />Excel</Button>
          </div>
        </div>

        {t && (
          <div className="stats-row">
            <div className="stat-chip"><i className="fas fa-box-open" style={{ color: "#dc2626" }} />Piezas faltantes <span>{numero(t.faltan)}</span></div>
            <div className="stat-chip" title={`${numero(t.faltan)} de ${numero(t.enviadas)} piezas enviadas y recibidas en el periodo`}>Del total enviado <span>{t.porcentaje}%</span></div>
            <div className="stat-chip">Traspasos con faltante <span>{numero(t.traspasos)} de {numero(t.traspasosRecibidos)}</span></div>
            <div className="stat-chip">Valor a precio <span>{dinero(t.valorPrecio)}</span></div>
            {verCostos && <div className="stat-chip">Valor a costo <span>{dinero(t.valorCosto)}</span></div>}
            <div className="stat-chip"><i className="fas fa-hourglass-half" style={{ color: "#d97706" }} />Por aclarar <span>{numero(t.pendientes)}</span></div>
          </div>
        )}

        {datos && datos.rutas.length > 0 && (
          <div className="form-section mb-3">
            <div className="form-section-header"><i className="fas fa-route" /><h4 className="form-section-title">Por ruta (quién envía → quién recibe)</h4></div>
            <div className="form-section-body table-responsive">
              <table className="table table-sm mb-0">
                <thead><tr><th>De</th><th>A</th><th className="text-right">Traspasos</th><th className="text-right">Piezas faltantes</th><th className="text-right">Valor a precio</th>{verCostos && <th className="text-right">Valor a costo</th>}<th className="text-right">Por aclarar</th></tr></thead>
                <tbody>
                  {datos.rutas.map((r) => (
                    <tr key={`${r.origen}-${r.destino}`}>
                      <td>{r.origen}</td><td>{r.destino}</td><td className="text-right">{numero(r.traspasos)}</td>
                      <td className="text-right" style={{ fontWeight: 700, color: "#dc2626" }}>{numero(r.faltan)}</td>
                      <td className="text-right">{dinero(r.valorPrecio)}</td>{verCostos && <td className="text-right">{dinero(r.valorCosto)}</td>}
                      <td className="text-right">{r.pendientes ? numero(r.pendientes) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="table-responsive">
          <table className="table table-sm table-dark-header">
            <thead>
              <tr><th>Traspaso</th><th>Ruta</th><th>Qué faltó</th><th className="text-right">Faltan</th><th className="text-right">Valor</th><th>Recepción</th><th>Aclaración</th></tr>
            </thead>
            <tbody>
              {(!datos || !datos.traspasos.length) && (
                <tr><td colSpan={7} className="text-center text-muted py-4">
                  {cargando || !datos ? "Cargando…" : <><i className="fas fa-check-circle mr-1" style={{ color: "#059669" }} />Sin faltantes en el periodo</>}
                </td></tr>
              )}
              {datos && datos.traspasos.map((x) => (
                <tr key={x._id} style={x.aclarado ? {} : { background: "#fffbeb" }}>
                  <td style={{ whiteSpace: "nowrap" }}><strong>{folioDe(x)}</strong><div className="small text-muted">{fechaHora(x.fecha)}</div></td>
                  <td>{x.origen.name} → {x.destino.name}<div className="small text-muted">Envió {nombre(x.envio) || "—"} · recibió {nombre(x.recibio) || "—"}</div></td>
                  <td style={{ fontSize: "0.82rem" }}>
                    {x.renglones.map((r, i) => (
                      <div key={i}><strong>{r.codigo}</strong> {variante(r)}: {r.recibidas} de {r.enviadas} <span style={{ color: "#dc2626", fontWeight: 700 }}>(−{r.faltan})</span></div>
                    ))}
                  </td>
                  <td className="text-right" style={{ fontWeight: 800, color: "#dc2626" }}>{numero(x.faltan)}</td>
                  <td className="text-right" style={{ whiteSpace: "nowrap" }}>{dinero(x.valorPrecio)}{verCostos && <div className="small text-muted">costo {dinero(x.valorCosto)}</div>}</td>
                  <td style={{ fontSize: "0.82rem" }}>{fechaHora(x.recibidoFecha)}{x.observacionesRecepcion && <div className="text-muted">"{x.observacionesRecepcion}"</div>}</td>
                  <td style={{ fontSize: "0.82rem", minWidth: 160 }}>
                    {x.aclarado ? (
                      <><span className="badge badge-success">Aclarado</span>
                        {x.aclarado.piezasRegresadas > 0 && <div style={{ color: "#059669", fontWeight: 600 }}>{numero(x.aclarado.piezasRegresadas)} pzs regresaron a {x.origen.name}</div>}
                        <div>{x.aclarado.nota}</div><div className="text-muted">{nombre(x.aclarado.user)} · {fechaHora(x.aclarado.fecha)}</div></>
                    ) : puedeAclarar ? (
                      <Button size="sm" color="primary" outline onClick={() => aclarar(x)}>Aclarar</Button>
                    ) : <span className="badge badge-warning">Por aclarar</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

export default PosFaltantes;
